<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Empleados</h1>
      <p class="text-gray-600 dark:text-gray-400">Catálogo de empleados POS2</p>
    </div>

    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <!-- Header -->
      <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-2 items-center justify-between">
        <div class="flex gap-2">
          <input
            v-model="filtros.buscar"
            @keyup.enter="buscar"
            type="text"
            placeholder="Buscar por nombre o correo..."
            class="px-3 py-2 rounded border text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 w-64"
          />
          <button @click="buscar" class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition">Buscar</button>
          <button @click="limpiar" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition">Limpiar</button>
        </div>
        <button
          @click="abrirModalNuevo"
          class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition flex items-center gap-1"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
          Nuevo Empleado
        </button>
      </div>

      <!-- Tabla -->
      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-b-2 border-gray-200 dark:border-gray-700">
            <tr>
              <th class="px-3 py-2 w-16">ID</th>
              <th class="px-3 py-2">Nombre Completo</th>
              <th class="px-3 py-2 w-32">RFC</th>
              <th class="px-3 py-2 w-48">Correo</th>
              <th class="px-3 py-2 w-32">Teléfono</th>
              <th class="px-3 py-2 w-40">Planta</th>
              <th class="px-3 py-2 w-24 text-center">Vendedor</th>
              <th class="px-3 py-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="px-4 py-8 text-center text-gray-400">Cargando...</td>
            </tr>
            <template v-else>
              <tr
                v-for="emp in empleados"
                :key="emp.id"
                class="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 bg-white dark:bg-gray-800"
              >
                <td class="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">{{ emp.id }}</td>
                <td class="px-3 py-2 font-medium text-gray-900 dark:text-white">
                  {{ emp.nombre }} {{ emp.apellido_paterno || '' }} {{ emp.apellido_materno || '' }}
                </td>
                <td class="px-3 py-2 font-mono text-gray-600 dark:text-gray-300 text-xs">{{ emp.rfc || '—' }}</td>
                <td class="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs truncate max-w-0">{{ emp.correo || '—' }}</td>
                <td class="px-3 py-2 text-gray-600 dark:text-gray-300 text-xs">{{ emp.telefono || '—' }}</td>
                <td class="px-3 py-2 text-gray-700 dark:text-gray-300 text-xs">
                  {{ emp.planta ? `${emp.planta.id_planta} - ${emp.planta.nombre}` : '—' }}
                </td>
                <td class="px-3 py-2 text-center">
                  <span v-if="emp.es_vendedor" class="text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full">Sí</span>
                  <span v-else class="text-xs font-semibold bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 px-2 py-0.5 rounded-full">No</span>
                </td>
                <td class="px-3 py-2">
                  <div class="flex gap-1">
                    <button @click="abrirModalEditar(emp)" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 p-1" title="Editar">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                    </button>
                    <button @click="eliminar(emp)" class="text-red-500 hover:text-red-700 dark:text-red-400 p-1" title="Eliminar">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="empleados.length === 0">
                <td colspan="8" class="px-4 py-8 text-center text-gray-400">No se encontraron empleados</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="pagination.total > 0" class="flex justify-between items-center px-4 py-3 border-t border-gray-200 dark:border-gray-700">
        <div class="text-sm text-gray-600 dark:text-gray-400">
          Mostrando {{ ((pagination.current_page - 1) * pagination.per_page) + 1 }}
          a {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }}
          de {{ pagination.total }} registros
        </div>
        <div class="flex gap-2">
          <button @click="cambiarPagina(pagination.current_page - 1)" :disabled="pagination.current_page <= 1" class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-sm disabled:opacity-50">Anterior</button>
          <span class="px-3 py-1 text-sm text-gray-600 dark:text-gray-400">Página {{ pagination.current_page }} de {{ pagination.last_page }}</span>
          <button @click="cambiarPagina(pagination.current_page + 1)" :disabled="pagination.current_page >= pagination.last_page" class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-sm disabled:opacity-50">Siguiente</button>
        </div>
      </div>
    </div>

    <!-- ===================== MODAL ===================== -->
    <div v-if="modalAbierto" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col">

        <!-- Modal header con tabs -->
        <div class="flex items-center justify-between px-6 pt-5 pb-0 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center gap-6">
            <h2 class="text-lg font-bold text-gray-900 dark:text-white mr-4">
              {{ editandoId ? 'Editar Empleado' : 'Nuevo Empleado' }}
            </h2>
            <button @click="tab = 'datos'" class="pb-3 text-sm font-semibold border-b-2 transition-colors"
              :class="tab === 'datos' ? 'border-violet-600 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'">
              Datos Personales
            </button>
            <button @click="tab = 'direccion'" class="pb-3 text-sm font-semibold border-b-2 transition-colors"
              :class="tab === 'direccion' ? 'border-violet-600 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'">
              Dirección
            </button>
            <button @click="tab = 'laboral'" class="pb-3 text-sm font-semibold border-b-2 transition-colors"
              :class="tab === 'laboral' ? 'border-violet-600 text-violet-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'">
              Laboral
            </button>
          </div>
          <button @click="cerrarModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 mb-3">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <!-- Modal body -->
        <div class="overflow-y-auto flex-1 px-6 py-5">
          <div v-if="formError" class="mb-4 px-4 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
            {{ formError }}
          </div>

          <!-- ---- TAB: DATOS PERSONALES ---- -->
          <div v-show="tab === 'datos'">
            <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Identificación</p>
            <div class="grid grid-cols-3 gap-4 mb-5">
              <div>
                <label class="form-label">Nombre <span class="text-red-500">*</span></label>
                <input v-model="form.nombre" type="text" maxlength="100" class="input-field" />
              </div>
              <div>
                <label class="form-label">Apellido Paterno</label>
                <input v-model="form.apellido_paterno" type="text" maxlength="100" class="input-field" />
              </div>
              <div>
                <label class="form-label">Apellido Materno</label>
                <input v-model="form.apellido_materno" type="text" maxlength="100" class="input-field" />
              </div>
              <div>
                <label class="form-label">RFC</label>
                <input v-model="form.rfc" type="text" maxlength="13" class="input-field font-mono uppercase" @input="form.rfc = form.rfc.toUpperCase()" placeholder="ABCD800101XX1" />
              </div>
              <div>
                <label class="form-label">Género</label>
                <select v-model="form.genero" class="input-field">
                  <option value="">Selecciona...</option>
                  <option value="Masculino">Masculino</option>
                  <option value="Femenino">Femenino</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
              <div>
                <label class="form-label">Estado Civil</label>
                <select v-model="form.estado_civil" class="input-field">
                  <option value="">Selecciona...</option>
                  <option value="Soltero">Soltero/a</option>
                  <option value="Casado">Casado/a</option>
                  <option value="Divorciado">Divorciado/a</option>
                  <option value="Viudo">Viudo/a</option>
                  <option value="Union libre">Unión libre</option>
                </select>
              </div>
              <div>
                <label class="form-label">Fecha de Nacimiento</label>
                <input v-model="form.fecha_nacimiento" type="date" class="input-field" />
              </div>
              <div class="col-span-2">
                <label class="form-label">Lugar de Nacimiento</label>
                <input v-model="form.lugar_nacimiento" type="text" maxlength="150" class="input-field" placeholder="Ciudad, Estado" />
              </div>
            </div>

            <p class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contacto</p>
            <div class="grid grid-cols-3 gap-4">
              <div class="col-span-2">
                <label class="form-label">Correo</label>
                <input v-model="form.correo" type="email" maxlength="150" class="input-field" />
              </div>
              <div>
                <label class="form-label">Teléfono</label>
                <input v-model="form.telefono" type="text" maxlength="30" class="input-field" />
              </div>
              <div class="col-span-3">
                <label class="form-label">Teléfono de Emergencia</label>
                <input v-model="form.telefono_emergencia" type="text" maxlength="30" class="input-field" />
              </div>
            </div>
          </div>

          <!-- ---- TAB: DIRECCIÓN ---- -->
          <div v-show="tab === 'direccion'">
            <div class="grid grid-cols-4 gap-4">
              <div class="col-span-4">
                <label class="form-label">Calle</label>
                <input v-model="form.calle" type="text" maxlength="150" class="input-field" />
              </div>
              <div>
                <label class="form-label">Núm. Exterior</label>
                <input v-model="form.num_exterior" type="text" maxlength="20" class="input-field" />
              </div>
              <div>
                <label class="form-label">Núm. Interior</label>
                <input v-model="form.num_interior" type="text" maxlength="20" class="input-field" />
              </div>
              <div class="col-span-2">
                <label class="form-label">Colonia</label>
                <input v-model="form.colonia" type="text" maxlength="100" class="input-field" />
              </div>
              <div>
                <label class="form-label">Código Postal</label>
                <input v-model="form.codigo_postal" type="text" maxlength="10" class="input-field font-mono" />
              </div>
            </div>
          </div>

          <!-- ---- TAB: LABORAL ---- -->
          <div v-show="tab === 'laboral'">
            <div class="grid grid-cols-2 gap-4 mb-5">
              <div>
                <label class="form-label">Planta</label>
                <select v-model="form.planta_id" class="input-field">
                  <option :value="null">— Sin planta —</option>
                  <option v-for="p in plantas" :key="p.id" :value="p.id">{{ p.id_planta }} - {{ p.nombre }}</option>
                </select>
              </div>
              <div>
                <label class="form-label">Fecha de Ingreso</label>
                <input v-model="form.fecha_ingreso" type="date" class="input-field" />
              </div>
              <div>
                <label class="form-label">NSS <span class="text-xs text-gray-400 font-normal">(IMSS, 11 dígitos)</span></label>
                <input v-model="form.nss" type="text" maxlength="11" class="input-field font-mono" placeholder="12345678901" />
              </div>
              <div class="flex items-center gap-3 mt-6">
                <label class="form-label mb-0">¿Es vendedor?</label>
                <button type="button" @click="form.es_vendedor = !form.es_vendedor"
                  class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                  :class="form.es_vendedor ? 'bg-violet-600' : 'bg-gray-300 dark:bg-gray-600'">
                  <span class="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform"
                    :class="form.es_vendedor ? 'translate-x-4' : 'translate-x-1'"></span>
                </button>
                <span class="text-sm font-semibold" :class="form.es_vendedor ? 'text-green-600' : 'text-gray-400'">
                  {{ form.es_vendedor ? 'Sí' : 'No' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal footer -->
        <div class="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button @click="cerrarModal" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition">Cancelar</button>
          <button @click="guardar" :disabled="guardando" class="px-5 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition disabled:opacity-60">
            {{ guardando ? 'Guardando...' : 'Guardar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useHttp } from '@/composables/useHttp.js'
import Swal from 'sweetalert2'

const httpEmpleados = useHttp()
const httpSave      = useHttp()
const httpPlantas   = useHttp()

const empleados  = ref([])
const plantas    = ref([])
const loading    = ref(false)
const guardando  = ref(false)
const filtros    = ref({ buscar: '' })
const pagination = ref({ current_page: 1, last_page: 1, per_page: 15, total: 0 })

const modalAbierto = ref(false)
const editandoId   = ref(null)
const tab          = ref('datos')
const formError    = ref('')

const formVacio = () => ({
  nombre: '', apellido_paterno: '', apellido_materno: '',
  fecha_nacimiento: '', correo: '', planta_id: null,
  fecha_ingreso: '', lugar_nacimiento: '',
  rfc: '', genero: '', estado_civil: '',
  calle: '', num_exterior: '', num_interior: '', colonia: '', codigo_postal: '',
  telefono: '', telefono_emergencia: '',
  nss: '', es_vendedor: false,
})
const form = ref(formVacio())

const cargarPlantas = async () => {
  await httpPlantas.get('/pos2/plantas?per_page=200')
  plantas.value = httpPlantas.data.value?.data ?? []
}

const cargar = async (pagina = 1) => {
  try {
    loading.value = true
    const params = new URLSearchParams({ page: pagina, per_page: pagination.value.per_page })
    if (filtros.value.buscar) params.append('buscar', filtros.value.buscar)
    await httpEmpleados.get(`/pos2/empleados?${params}`)
    empleados.value = httpEmpleados.data.value.data
    pagination.value = {
      current_page: httpEmpleados.data.value.current_page,
      last_page:    httpEmpleados.data.value.last_page,
      per_page:     httpEmpleados.data.value.per_page,
      total:        httpEmpleados.data.value.total,
    }
  } finally {
    loading.value = false
  }
}

const buscar        = () => cargar(1)
const limpiar       = () => { filtros.value.buscar = ''; cargar(1) }
const cambiarPagina = (p) => { if (p >= 1 && p <= pagination.value.last_page) cargar(p) }

const abrirModalNuevo = () => {
  editandoId.value = null
  form.value = formVacio()
  tab.value = 'datos'
  formError.value = ''
  modalAbierto.value = true
}

const abrirModalEditar = (emp) => {
  editandoId.value = emp.id
  form.value = {
    nombre:              emp.nombre ?? '',
    apellido_paterno:    emp.apellido_paterno ?? '',
    apellido_materno:    emp.apellido_materno ?? '',
    fecha_nacimiento:    emp.fecha_nacimiento ?? '',
    correo:              emp.correo ?? '',
    planta_id:           emp.planta_id ?? null,
    fecha_ingreso:       emp.fecha_ingreso ?? '',
    lugar_nacimiento:    emp.lugar_nacimiento ?? '',
    rfc:                 emp.rfc ?? '',
    genero:              emp.genero ?? '',
    estado_civil:        emp.estado_civil ?? '',
    calle:               emp.calle ?? '',
    num_exterior:        emp.num_exterior ?? '',
    num_interior:        emp.num_interior ?? '',
    colonia:             emp.colonia ?? '',
    codigo_postal:       emp.codigo_postal ?? '',
    telefono:            emp.telefono ?? '',
    telefono_emergencia: emp.telefono_emergencia ?? '',
    nss:                 emp.nss ?? '',
    es_vendedor:         !!emp.es_vendedor,
  }
  tab.value = 'datos'
  formError.value = ''
  modalAbierto.value = true
}

const cerrarModal = () => {
  modalAbierto.value = false
  editandoId.value = null
  formError.value = ''
}

const guardar = async () => {
  if (!form.value.nombre.trim()) {
    formError.value = 'El nombre es obligatorio'
    tab.value = 'datos'
    return
  }
  // Limpiar strings vacíos a null para campos opcionales
  const payload = { ...form.value }
  Object.keys(payload).forEach(k => {
    if (typeof payload[k] === 'string' && payload[k].trim() === '') payload[k] = null
  })

  try {
    guardando.value = true
    formError.value = ''
    if (editandoId.value) {
      await httpSave.put(`/pos2/empleados/${editandoId.value}`, payload)
      const updated = httpSave.data.value
      const idx = empleados.value.findIndex(e => e.id === editandoId.value)
      if (idx !== -1) empleados.value.splice(idx, 1, updated)
    } else {
      await httpSave.post('/pos2/empleados', payload)
      const created = httpSave.data.value
      empleados.value = [created, ...empleados.value]
    }
    cerrarModal()
  } catch (err) {
    const errors = err.response?.data?.errors
    formError.value = errors
      ? Object.values(errors).flat().join(' | ')
      : (err.response?.data?.message ?? 'Error al guardar')
  } finally {
    guardando.value = false
  }
}

const eliminar = async (emp) => {
  const result = await Swal.fire({
    title: '¿Estás seguro?',
    text: `¿Deseas eliminar a "${emp.nombre} ${emp.apellido_paterno ?? ''}"?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar',
  })
  if (result.isConfirmed) {
    try {
      await httpSave.del(`/pos2/empleados/${emp.id}`)
      cargar(pagination.value.current_page)
    } catch {
      Swal.fire('Error', 'No se pudo eliminar', 'error')
    }
  }
}

onMounted(() => {
  cargarPlantas()
  cargar()
})
</script>

<style scoped>
.form-label {
  @apply block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1;
}
.input-field {
  @apply w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm;
}
</style>
